import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, Globe } from 'lucide-react';
import {
  LANGUAGES,
  SUPPORTED_LANGUAGES,
  addLanguagePrefix,
  isPublicPath,
  normalizeLanguage,
} from '../i18n/config';
import { changeLanguage } from '../i18n';

/**
 * Language picker.
 *
 * A menu rather than the two-segment control the theme uses: three options
 * with non-Latin labels do not fit a segmented switch, and the list has to
 * show each language in its own script — someone who cannot read the current
 * UI language has to be able to find their way out, which is exactly the
 * situation a switcher exists for. Hence the native name is the primary
 * label, and it renders in its own direction regardless of the active one.
 *
 * `compact` drops the label to just the globe + code, for the app header
 * where it competes with install/bell/avatar for room.
 */
export default function LanguageSwitcher({ compact = false, className = '' }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const ref = useRef(null);

  const active = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const activeMeta = LANGUAGES[active];

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const select = useCallback(
    async (code) => {
      setPending(code);
      try {
        // Loads the catalogue before switching, so the UI never flashes
        // half-translated. See changeLanguage in src/i18n.
        await changeLanguage(code);

        // On a page that has a per-language address, move to it. Leaving the
        // reader on /terms while the content is Arabic would make the URL lie
        // — and it is the URL they would copy, bookmark or share.
        //
        // The authenticated app has no prefixed routes, so there is nothing to
        // navigate to there and the language change alone is the whole job.
        if (isPublicPath(pathname)) {
          const next = addLanguagePrefix(pathname, code);
          if (next !== pathname) navigate(next, { replace: true });
        }

        setOpen(false);
      } finally {
        setPending(null);
      }
    },
    [navigate, pathname]
  );

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.change')}
        title={t('language.change')}
        className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-jc-border bg-jc-elevated text-jc-secondary hover:text-jc hover:bg-jc-hover transition"
      >
        <Globe className="w-4 h-4 shrink-0" />
        {compact ? (
          <span className="text-xs font-semibold uppercase">{active}</span>
        ) : (
          <span className="text-sm font-medium">{activeMeta.nativeName}</span>
        )}
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t('language.label')}
          /* Pinned to the inline-end edge so the menu opens inward in both
             directions — `end-0` is right in LTR and left in RTL. */
          className="absolute end-0 mt-2 w-48 py-1 bg-jc-card border border-jc-border rounded-xl shadow-jc-lg overflow-hidden z-50"
        >
          {SUPPORTED_LANGUAGES.map((code) => {
            const meta = LANGUAGES[code];
            const isActive = code === active;
            return (
              <li key={code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  disabled={pending !== null}
                  onClick={() => select(code)}
                  className={[
                    'w-full flex items-center justify-between gap-3 px-3 py-2.5 text-start transition',
                    isActive
                      ? 'bg-jc-primary-soft text-jc-primary font-semibold'
                      : 'text-jc-secondary hover:bg-jc-hover hover:text-jc',
                    pending !== null ? 'opacity-60 cursor-wait' : '',
                  ].join(' ')}
                >
                  <span className="min-w-0">
                    {/* Each option carries its own lang/dir so the native name
                        shapes and orders correctly whatever the active UI
                        language is — an Arabic label inside an English menu
                        still has to read right-to-left. */}
                    <span
                      lang={code}
                      dir={meta.dir}
                      className="block text-sm truncate"
                    >
                      {meta.nativeName}
                    </span>
                    <span className="block text-xs text-jc-muted truncate">{meta.label}</span>
                  </span>
                  {isActive && <Check className="w-4 h-4 shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
